import { AwsAdjustment, OnPremAdjustment } from '@amzn/awscat-portfolio-assessment-types';
import { Box, Button, Container, FormField, Header, Input, RadioGroup, Select, SpaceBetween, Textarea, Toggle } from '@amzn/awsui-components-react';
import { validate, ValidationError } from 'class-validator';
import { ForwardedRef, forwardRef, useImperativeHandle, useState } from 'react';

import { constants } from '../../../common/Localization';
import { getCurrencySymbol } from '../../../common/LocalizationHelper';
import { errorLookup, GetErrorText } from '../../../common/validator/ErrorLookup';
import ConfirmationModal from '../../common/ConfirmationModal';
import { awsServicesMap, frequencyOptions, getAwsServicesOptions, getCostCategoryOptions, getDefaultNewAdjustment } from './constants';
import { AdjustmentType, AwsOnPremAdjustment, ForwardRefFnType,SelectOption } from './interfaces';

interface Props {
    type: AdjustmentType;
    currency: string;
    existingAdjustment?: AwsOnPremAdjustment;
}

enum awsTitleOptions {
    AWS_SERVICE = 'awsService',
    OTHER = 'other'
}

const AddEditAdjustmentItem = ({ type, currency, existingAdjustment }: Props, ref: ForwardedRef<ForwardRefFnType>): JSX.Element => {

    // expose these two functions to parent component
    useImperativeHandle(ref, () => ({
        validate: async (): Promise<boolean> => {
            return await validateFields();
        },
        getAdjustment: (): AwsOnPremAdjustment | null => {
            return areFieldsVisible ? adjustmentToUpdate : null;
        }
    }));

    const costCategoryOptions = getCostCategoryOptions(type);
    const awsServiceOptions = getAwsServicesOptions();

    const [ areFieldsVisible, setAreFieldsVisible ] = useState(existingAdjustment !== undefined);
    const [ getErrorText, setGetErrorText ] = useState<GetErrorText<AwsAdjustment>>(() => () => '');
    const [ adjustmentToUpdate, setAdjustmentToUpdate ] = useState<AwsOnPremAdjustment>(existingAdjustment ?? getDefaultNewAdjustment(type));
    const [ awsServiceTitleRadioSelection, setAwsServiceTitleRadioSelection ] = 
        useState<awsTitleOptions>(!existingAdjustment || awsServicesMap.get(existingAdjustment?.title ?? '') 
            ? awsTitleOptions.AWS_SERVICE : awsTitleOptions.OTHER);
    const [ selectedService, setSelectedService ] = 
        useState(awsServiceOptions.find(option => option.value === existingAdjustment?.title) ?? awsServiceOptions[0]);
    const [ selectedCostCategory, setSelectedCostCategory ] = 
        useState(costCategoryOptions.find(option => option.value === existingAdjustment?.category) ?? costCategoryOptions[0]);
    const [ selectedFrequency, setSelectedFrequency ] = 
        useState(frequencyOptions.find(option => option.value === existingAdjustment?.frequency) ?? frequencyOptions[1]);
    const [ titleInput, setTitleInput ] = useState(existingAdjustment?.title ?? '');
    const [ showDeleteFriction, setShowDeleteFriction ] = useState(false);
   
    const updateAdjustmentProperty = (value: string | boolean, property: keyof AwsAdjustment): void => {
        setAdjustmentToUpdate(adjustment => {
            const updatedAdjustment = {...adjustment, [property]: value};
            validateFieldsIfErrorsExist(updatedAdjustment, property);
            return updatedAdjustment;
        });
    };

    const validateFieldsIfErrorsExist = (updatedAdjustment: AwsOnPremAdjustment, propertyChanged: keyof AwsAdjustment): void => {
        if (updatedAdjustment && propertyChanged && getErrorText(propertyChanged)?.length === 0) {
            return;
        }
        validateFields(updatedAdjustment);
    };

    const validateFields = async (updatedAdjustment?: AwsOnPremAdjustment): Promise<boolean> => {
        if (!areFieldsVisible) {
            return true;
        }

        let validationErrors: ValidationError[];

        if (type === AdjustmentType.AWS) {
            validationErrors = await validate(Object.assign(new AwsAdjustment(), updatedAdjustment ?? adjustmentToUpdate));
        } else {
            validationErrors = await validate(Object.assign(new OnPremAdjustment(), updatedAdjustment ?? adjustmentToUpdate));
        }

        const getErrorTextFn = errorLookup<AwsAdjustment>(validationErrors);
        setGetErrorText(() => getErrorTextFn);
        return validationErrors.length === 0;
    };

    const awsSelectedServiceChanged = (selectedOption: SelectOption): void => {
        setSelectedService(selectedOption);

        updateAdjustmentProperty(selectedOption.value, 'title');
        const serviceCategory = awsServicesMap.get(selectedOption.value);
        const categoryOption = costCategoryOptions.find(option => option.value === serviceCategory?.category);
        
        if (categoryOption && categoryOption.value) {
            setSelectedCostCategory(categoryOption);
            updateAdjustmentProperty(categoryOption.value, 'category');
        }
    };

    const awsTitleRadioOptionChanged = (option: awsTitleOptions): void => {
        setAwsServiceTitleRadioSelection(option);

        if (option === awsTitleOptions.AWS_SERVICE) {
            updateAdjustmentProperty(selectedService.value, 'title');
        } else {
            updateAdjustmentProperty(titleInput, 'title');
        }
    };

    const onDeleteClick = () => {
        if (!existingAdjustment) {
            setAreFieldsVisible(false);
        } else {
            setShowDeleteFriction(true);
        }
    };

    const onConfirmDelete = () => {
        setAreFieldsVisible(false);
        setShowDeleteFriction(false);
    };

    const titleInputField = (
        <Input
            data-testid='titleInput'
            value={titleInput} 
            onChange={({ detail }) => {
                setTitleInput(detail.value);
                updateAdjustmentProperty(detail.value, 'title');
            }}
        />
    );

    const awsTitle = areFieldsVisible && (
        <SpaceBetween size='s'>
            <RadioGroup
                value={awsServiceTitleRadioSelection}
                items={[
                    {
                        label: constants.AWS_SERVICE,
                        value: awsTitleOptions.AWS_SERVICE
                    },
                    {
                        label: constants.OTHER,
                        value: awsTitleOptions.OTHER
                    }
                ]}
                onChange={({ detail }) => awsTitleRadioOptionChanged(detail.value as awsTitleOptions)}
            />
            {
                awsServiceTitleRadioSelection === awsTitleOptions.AWS_SERVICE ? (
                    <Select
                        data-testid='selectAWSService'
                        selectedOption={selectedService}
                        options={getAwsServicesOptions()}
                        onChange={({ detail }) => awsSelectedServiceChanged(detail.selectedOption as SelectOption)}
                        filteringType='auto'
                    />
                ) : titleInputField
            }
        </SpaceBetween>
    );

    const titleField = areFieldsVisible && (
        <FormField
            description={type === AdjustmentType.AWS ? constants.SERVICE_DESCRIPTION: constants.SHORT_DESCRIPTION}
            label={constants.TITLE}
            errorText={getErrorText('title')}
        >
            {type === AdjustmentType.AWS ? awsTitle : titleInputField}
        </FormField>
    );

    const costCategoryField = areFieldsVisible && (
        <FormField
            label={constants.COST_CATEGORY}
            errorText={getErrorText('category')}
        >
            <Select
                data-testid='selectCategory'
                selectedOption={selectedCostCategory}
                options={costCategoryOptions}
                onChange={({ detail }) => {
                    if (detail.selectedOption.value) {
                        setSelectedCostCategory(detail.selectedOption);
                        updateAdjustmentProperty(detail.selectedOption.value, 'category');
                    }
                }}
                disabled={awsServiceTitleRadioSelection === awsTitleOptions.AWS_SERVICE && type === AdjustmentType.AWS}
            />
        </FormField>
    );

    const content = areFieldsVisible ? (
        <form>
            <SpaceBetween size='m'>
                {titleField}
                {costCategoryField}
                <FormField
                    description={constants.DETAILED_DESCRIPTION}
                    label={constants.DESCRIPTION}
                    errorText={getErrorText('description')}
                >
                    <Textarea
                        data-testid='description'
                        value={adjustmentToUpdate.description}
                        onChange={({ detail }) => updateAdjustmentProperty(detail.value, 'description')}
                    />
                </FormField>
                <FormField
                    description={constants.AMOUNT_DESCRIPTION}
                    label={`${constants.AMOUNT} (${getCurrencySymbol(currency)})`}
                    errorText={getErrorText('amount')}
                >
                    <Input
                        data-testid='amount'
                        value={adjustmentToUpdate.amount.toString()} 
                        onChange={({ detail }) => updateAdjustmentProperty(detail.value, 'amount')} 
                        type='number'
                    />
                </FormField>
                <FormField
                    description={constants.FREQUENCY_DESCRIPTION}
                    label={constants.FREQUENCY}
                    errorText={getErrorText('frequency')}
                >
                    <Select
                        data-testid='frequency'
                        options={frequencyOptions}
                        selectedOption={selectedFrequency}
                        onChange={({ detail }) => {
                            if (detail.selectedOption.value) {
                                setSelectedFrequency(detail.selectedOption);
                                updateAdjustmentProperty(detail.selectedOption.value, 'frequency');
                            }
                        }}
                    />
                </FormField>
                {type === AdjustmentType.AWS && 'awsSupport' in adjustmentToUpdate && (
                    <FormField
                        errorText={getErrorText('awsSupport' as keyof AwsOnPremAdjustment)}
                    >
                        <Toggle
                            data-testid='awsSupport'
                            checked={adjustmentToUpdate.awsSupport}
                            description={constants.AWS_SUPPORT_DESCRIPTION}
                            onChange={({ detail }) => updateAdjustmentProperty(detail.checked, 'awsSupport')}
                        >
                            {constants.CONSIDERED_FOR_AWS_SUPPORT}
                        </Toggle>
                    </FormField>
                )}
                <Toggle
                    data-testid='inScope'
                    checked={adjustmentToUpdate.inScope}
                    description={constants.IN_SCOPE_ADJUSTMENT_DESCRIPTION}
                    onChange={({ detail }) => updateAdjustmentProperty(detail.checked, 'inScope')}
                >
                    {constants.IN_SCOPE}
                </Toggle>
            </SpaceBetween>
        </form>
    ) : (
        <Box textAlign='center'>
            <Button
                data-testid='showFieldsBtn'
                onClick={() => setAreFieldsVisible(true)}
            >
                {`${constants.ADD_CORRESPONDING} ${type} ${constants.COST_LOWERCASE}`}
            </Button>
        </Box>
    );

    return (
        <Container
            header={
                <Header 
                    actions={ areFieldsVisible && (
                        <Button
                            data-testid='closeBtn'
                            iconName='close'
                            variant='icon'
                            onClick={onDeleteClick}
                        />
                    )}
                >
                    {type === AdjustmentType.AWS ? constants.IN_AWS : constants.ON_PREMISES}
                </Header>
            }
        >
            {content}
            { showDeleteFriction ? (
                <ConfirmationModal
                    confirmButton={constants.DELETE_BUTTON_TEXT}
                    onConfirm={onConfirmDelete}
                    onDismiss={() => setShowDeleteFriction(false)}
                    header={constants.DELETE_ADJUSTMENT}
                    visible
                >
                    {constants.DELETE_ADJUSTMENT_TEXT}
                </ConfirmationModal>
            ) : null }
        </Container>
    );
};

export default forwardRef(AddEditAdjustmentItem);
